import { motion } from 'framer-motion'
import { useState } from 'react'
import { Code, Shield, Zap, Globe, TrendingUp, AlertTriangle, BookOpen, Heart } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface AICapability {
  name: string
  level: number
  maxLevel: number
  description: string
  icon: React.ComponentType<any>
  color: string
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
}

const AICapabilitiesRadar = () => {
  const { t } = useLanguage()
  const [selectedCapability, setSelectedCapability] = useState<AICapability | null>(null)

  const capabilities: AICapability[] = [
    {
      name: t('capabilities.coding'),
      level: 85,
      maxLevel: 100,
      description: 'Autonomous software development, debugging, and optimization across multiple programming languages',
      icon: Code,
      color: 'from-blue-500 to-cyan-500',
      riskLevel: 'medium'
    },
    {
      name: t('capabilities.hacking'),
      level: 45,
      maxLevel: 100,
      description: 'Cybersecurity analysis, vulnerability detection, and defensive capabilities',
      icon: Shield,
      color: 'from-red-500 to-pink-500',
      riskLevel: 'high'
    },
    {
      name: t('capabilities.research'),
      level: 78,
      maxLevel: 100,
      description: 'Scientific research, data analysis, and knowledge synthesis across domains',
      icon: BookOpen,
      color: 'from-purple-500 to-indigo-500',
      riskLevel: 'low'
    },
    {
      name: t('capabilities.automation'),
      level: 72,
      maxLevel: 100,
      description: 'Process automation, workflow optimization, and task orchestration',
      icon: Zap,
      color: 'from-yellow-500 to-orange-500',
      riskLevel: 'medium'
    },
    {
      name: t('capabilities.socialIntelligence'),
      level: 68,
      maxLevel: 100,
      description: 'Understanding and responding to human emotions',
      icon: Heart,
      color: 'from-green-500 to-emerald-500',
      riskLevel: 'high'
    },
    {
      name: t('capabilities.globalImpact'),
      level: 37,
      maxLevel: 100,
      description: 'Large-scale societal and economic effects',
      icon: Globe,
      color: 'from-indigo-500 to-purple-500',
      riskLevel: 'critical'
    }
  ]

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'low': return 'text-green-600 bg-green-100'
      case 'medium': return 'text-yellow-600 bg-yellow-100'
      case 'high': return 'text-orange-600 bg-orange-100'
      case 'critical': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getRiskIcon = (riskLevel: string) => {
    return riskLevel === 'critical' || riskLevel === 'high' ? AlertTriangle : TrendingUp
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="bg-white rounded-2xl shadow-lg p-8"
    >
      <div className="mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">{t('capabilities.title')}</h2>
        <p className="text-base sm:text-lg text-gray-600 px-4">{t('capabilities.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Capabilities Grid */}
        <div className="space-y-6">
          {capabilities.map((capability, index) => {
            const RiskIcon = getRiskIcon(capability.riskLevel)
            return (
              <motion.div
                key={capability.name}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedCapability?.name === capability.name
                    ? 'border-ai-300 bg-ai-50'
                    : 'border-gray-200 hover:border-ai-200 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedCapability(
                  selectedCapability?.name === capability.name ? null : capability
                )}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 bg-gradient-to-r ${capability.color} rounded-lg flex items-center justify-center`}>
                      <capability.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{capability.name}</h4>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(capability.riskLevel)}`}>
                          {capability.riskLevel}
                        </span>
                        <RiskIcon className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-purple-600 mb-1">{capability.level}%</div>
                    <div className="text-sm text-gray-500">{t('capabilities.ofHumanLevel')}</div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <motion.div
                    className={`h-3 bg-gradient-to-r ${capability.color} rounded-full`}
                    initial={{ width: 0 }}
                    whileInView={{ width: `${capability.level}%` }}
                    transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                    viewport={{ once: true }}
                  />
                </div>

                {/* Description (shown when selected) */}
                {selectedCapability?.name === capability.name && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 pt-3 border-t border-gray-200"
                  >
                    <p className="text-sm text-gray-600">{capability.description}</p>
                  </motion.div>
                )}
              </motion.div>
            )
          })}
        </div>

        {/* Visualization */}
        <div className="flex flex-col justify-center">
          <div className="relative w-80 h-80 mx-auto">
            {/* Radar chart background */}
            <svg className="w-full h-full" viewBox="0 0 320 320">
              {/* Grid circles */}
              {[1, 2, 3, 4, 5].map((ring) => (
                <circle
                  key={ring}
                  cx="160"
                  cy="160"
                  r={ring * 30}
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="1"
                />
              ))}
              
              {/* Grid lines */}
              {capabilities.map((_, index) => {
                const angle = (index * 360) / capabilities.length - 90
                const radian = (angle * Math.PI) / 180
                const x2 = 160 + Math.cos(radian) * 150
                const y2 = 160 + Math.sin(radian) * 150
                return (
                  <line
                    key={index}
                    x1="160"
                    y1="160"
                    x2={x2}
                    y2={y2}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                  />
                )
              })}

              {/* Data polygon */}
              <polygon
                points={capabilities.map((capability, index) => {
                  const angle = (index * 360) / capabilities.length - 90
                  const radian = (angle * Math.PI) / 180
                  const distance = (capability.level / 100) * 150
                  const x = 160 + Math.cos(radian) * distance
                  const y = 160 + Math.sin(radian) * distance
                  return `${x},${y}`
                }).join(' ')}
                fill="url(#radarGradient)"
                fillOpacity="0.3"
                stroke="#d946ef"
                strokeWidth="2"
              />

              {/* Data points */}
              {capabilities.map((capability, index) => {
                const angle = (index * 360) / capabilities.length - 90
                const radian = (angle * Math.PI) / 180
                const distance = (capability.level / 100) * 150
                const x = 160 + Math.cos(radian) * distance
                const y = 160 + Math.sin(radian) * distance
                return (
                  <circle
                    key={index}
                    cx={x}
                    cy={y}
                    r="4"
                    fill="#d946ef"
                    className="cursor-pointer hover:r-6 transition-all"
                    onClick={() => setSelectedCapability(capability)}
                  />
                )
              })}

              {/* Gradient definition */}
              <defs>
                <radialGradient id="radarGradient">
                  <stop offset="0%" stopColor="#d946ef" />
                  <stop offset="100%" stopColor="#0ea5e9" />
                </radialGradient>
              </defs>
            </svg>

            {/* Labels */}
            {capabilities.map((capability, index) => {
              const angle = (index * 360) / capabilities.length - 90
              const radian = (angle * Math.PI) / 180
              const x = 160 + Math.cos(radian) * 170
              const y = 160 + Math.sin(radian) * 170
              return (
                <div
                  key={capability.name}
                  className="absolute text-xs font-medium text-gray-700 transform -translate-x-1/2 -translate-y-1/2"
                  style={{ left: x, top: y }}
                >
                  {capability.name}
                </div>
              )
            })}
          </div>

          {/* Legend */}
          <div className="mt-6 text-center">
            <h4 className="text-lg font-bold text-gray-900 mb-4">{t('capabilities.riskLegend')}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">{t('capabilities.low')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm text-gray-600">{t('capabilities.medium')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-sm text-gray-600">{t('capabilities.high')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-600">{t('capabilities.critical')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default AICapabilitiesRadar 